import React, {useCallback} from 'react';
import {Layout, Button, Spacer, Text, Ruler, Tooltip} from '@di-nxt/components';
import {t} from '../../../localization';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {ScheduleCalendarType} from '../../../services';
import {UnassignedButton} from './unassignedTasksSidebar/UnassignedButton';
import {RequestOptimizerButton} from './RequestOptimizerButton';

dayjs.extend(localizedFormat);

type Props = {
  teamId: number;
  selectedDate: Date;
  calendarType: ScheduleCalendarType;
  showButton: boolean;
  dailyUnassignedTasks?: number;
  onSelectedDateChange: (nextDate: Date) => void;
  onShowUnassignedTasks: () => void;
};

export const ScheduleCalendarToolbar: React.FC<Props> = ({
  teamId,
  selectedDate,
  calendarType,
  showButton,
  dailyUnassignedTasks,
  onSelectedDateChange,
  onShowUnassignedTasks,
}) => {
  // Handle date change
  const handleNext = useCallback(
    () =>
      onSelectedDateChange(dayjs(selectedDate).add(1, calendarType).toDate()),
    [calendarType, onSelectedDateChange, selectedDate],
  );

  const handlePrev = useCallback(
    () =>
      onSelectedDateChange(dayjs(selectedDate).add(-1, calendarType).toDate()),
    [calendarType, onSelectedDateChange, selectedDate],
  );

  const handleToday = useCallback(() => onSelectedDateChange(new Date()), [
    onSelectedDateChange,
  ]);

  return (
    <Layout horizontal centered paddingHorizontal="20" paddingVertical="12">
      <Layout flex>
        {calendarType === 'day' && (
          <Text variant="inline_16_default">
            {dayjs(selectedDate).format('ll')}
          </Text>
        )}
        {calendarType !== 'day' && (
          <Text variant="inline_16_default">
            {dayjs(selectedDate).format('MMMM YYYY')}
          </Text>
        )}
      </Layout>
      <Layout>
        {calendarType === 'week' && (
          <Layout>
            <RequestOptimizerButton
              teamId={teamId}
              calendarType={calendarType}
              selectedDate={selectedDate}
            />
          </Layout>
        )}
        {!showButton && calendarType === 'day' && (
          <>
            <Tooltip
              tooltipText={t('screens.schedule.tooltip.unassigned', {
                tasks: dailyUnassignedTasks
                  ? dailyUnassignedTasks?.toString()
                  : '0',
              })}
              trigger={
                <>
                  <UnassignedButton
                    onPress={onShowUnassignedTasks}
                    badgeNumber={
                      dailyUnassignedTasks ? dailyUnassignedTasks : 0
                    }
                  />
                </>
              }
              arrowPosition="TopRight"
            />
            <Spacer width="8" />
            <Ruler vertical width="1" />
            <Spacer width="8" />
          </>
        )}
        <Button
          kind="Clear"
          onPress={handlePrev}
          size="Small"
          leftIcon="arrowLeft"
        />
        <Spacer width="8" />
        <Button
          onPress={handleToday}
          kind="Secondary"
          size="Small"
          title={t('screens.schedule.todaybutton')}
        />

        <Spacer width="8" />
        <Button
          kind="Clear"
          onPress={handleNext}
          size="Small"
          leftIcon="arrowRight"
        />
      </Layout>
    </Layout>
  );
};
